import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as R from 'remeda';
import { combineLatest, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Frac } from '~lmo/models/frac.model';
import { FracClusterPurchaseOrder, PurchaseOrder } from '~lmo/models/purchaseOrders.model';
import { Report } from '~lmo/models/report.model';
import { LmoConstantsService } from '~lmo/services/lmo-constants.service';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoReportsService } from '~lmo/services/lmo-reports.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';
import { Mesh } from '~v2Models/mesh.model';
import { FracMine } from '~v2Models/mine.model';
import { LmoAutoProcurementDialogComponent } from '../lmo-auto-procurement-dialog/lmo-auto-procurement-dialog.component';

interface FcpoWithMineWaitTime extends FracClusterPurchaseOrder {
  mineWaitTime: number;
  downReport?: Report;
  isClosed: boolean;
  defaultLoadWeight?: number;
  billableMileage: number;
}

interface MeshWithFcpos extends Mesh {
  fcpos: FcpoWithMineWaitTime[];
  maximumDailyAllocation: number;
  orderedToday: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-home-purchase-orders',
  styleUrls: ['./lmo-frac-home-purchase-orders.component.scss'],
  templateUrl: './lmo-frac-home-purchase-orders.component.html',
})
export class LmoFracHomePurchaseOrdersComponent implements OnInit {
  @Input() public frac: any;

  public meshesWithFcpos$: Observable<MeshWithFcpos[]>;
  public trackById = trackById;
  public showPopover: boolean;
  public userIsAdmin: boolean;

  constructor(
    private fcpoService: LmoFracFcpoService,
    private constantsService: LmoConstantsService,
    private reports: LmoReportsService,
    private matDialog: MatDialog,
    private userService: UserService,
  ) {
    if (this.userService.isAdmin() || this.userService.isDefault() || this.userService.isViewOnly()) {
      this.showPopover = true;
    }
    if (this.userService.isAdmin()) {
      this.userIsAdmin = true;
    }
  }

  public ngOnInit() {
    this.meshesWithFcpos$ = combineLatest([
      this.fcpoService.currentFracFcpos$,
      this.constantsService.meshesById$,
      of(this.frac),
      this.reports.reports$,
    ]).pipe(map(mergeMeshWithFcpo), map(R.sort(sortByPreferredOrder)), tap(addTotalsToMeshes));
  }

  public isMaxedOut(po: PurchaseOrder): boolean {
    return this.totalOver(po) || this.dailyOver(po);
  }

  public totalOver(po: PurchaseOrder): boolean {
    if (!po) {
      return false;
    }
    return (po.totalAllocationUsed || 0) > po.allocation;
  }

  public dailyOver(po: PurchaseOrder): boolean {
    if (!po) {
      return false;
    }
    return (po.orderedToday || 0) > po.maximumDailyAllocation;
  }

  public toggleAutoProcurementDialog() {
    this.matDialog.open(LmoAutoProcurementDialogComponent);
  }
}

function mergeMeshWithFcpo([fcpos, meshes, frac, reports]: [
  FracClusterPurchaseOrder[],
  Mesh[],
  Frac,
  Report[],
]): MeshWithFcpos[] {
  if (!fcpos || !meshes || !frac || !reports) {
    return [];
  }
  const minesRecord: Record<number, FracMine> = (frac.fracMines || []).reduce((record, mine) => {
    record[mine.id] = mine;
    return record;
  }, {});
  const headerRecord: Record<number, FracClusterPurchaseOrder[]> = (fcpos || []).reduce(
    (record: Record<number, FracClusterPurchaseOrder[]>, fcpo) => {
      if (!record[fcpo.purchaseOrder.meshId]) {
        record[fcpo.purchaseOrder.meshId] = [];
      }
      const mine = minesRecord[fcpo.purchaseOrder.mineId];
      const fcpoWithMineWaitTime: FcpoWithMineWaitTime = {
        ...fcpo,
        billableMileage: mine ? mine.billableMileage : null,
        defaultLoadWeight: fcpo.defaultLoadWeights ? fcpo.defaultLoadWeights[frac.id] : null,
        isClosed: mine ? mine.isClosed : false,
        mineWaitTime: mine ? mine.waitTime : 0,
      };
      const mineSite = minesRecord[fcpo.purchaseOrder.mineId];
      if (mineSite) {
        const activeReport = reports.find((r) => {
          return (
            r.available === false && r.mesh && r.mesh.id === fcpo.purchaseOrder.meshId && r.site.id === mineSite.site.id
          );
        });
        if (activeReport) {
          fcpoWithMineWaitTime.downReport = activeReport;
        }
      }
      record[fcpo.purchaseOrder.meshId].push(fcpoWithMineWaitTime);
      return record;
    },
    {},
  );
  return Object.keys(headerRecord).map((key) => ({
    ...meshes[key],
    fcpos: headerRecord[key],
  }));
}

function addTotalsToMeshes(meshesWithFcpos: MeshWithFcpos[]): void {
  meshesWithFcpos.forEach((meshWithFcpo) => {
    meshWithFcpo.maximumDailyAllocation = meshWithFcpo.fcpos
      .map((fcpo) => fcpo.purchaseOrder.maximumDailyAllocation || 0)
      .reduce((sum, allocation) => sum + allocation, 0);
    meshWithFcpo.orderedToday = meshWithFcpo.fcpos
      .map((fcpo) => fcpo.purchaseOrder.orderedToday || 0)
      .reduce((sum, allocation) => sum + allocation, 0);
  });
}

function sortByPreferredOrder<T extends { preferredOrder: number }>(a: T, b: T): number {
  return a.preferredOrder < b.preferredOrder ? -1 : 1;
}
